// Scoped theme for the AI-native grading screens, applied via a local
// <ThemeProvider> over the grading subtree ONLY (never mutates the global theme).
// The token VALUES below are aligned to the Yudu design system (brand blue/green/
// yellow, neutral surfaces, Inter sans) so the screens look native to the app.
// The `ashbrook` / `ashbrookType` names are kept only so the screens keep their
// existing token references — this is purely a palette/typography realignment.
import { createTheme } from '@mui/material/styles';
import { tokens } from '@utils/tokens';

/** HG logo segment colours — purple / orange / green. */
export const hgPalette = [tokens.hgPurple, tokens.hgOrange, tokens.hgLimeGreen] as const;

/**
 * Pick an HG brand colour for a student.
 * Pass a number (list index) for ordered lists, or a string (name/id) for
 * stable per-student colouring across independent renders.
 */
export function hgColor(seed: string | number): string {
  if (typeof seed === 'number') return hgPalette[((seed % 3) + 3) % 3];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h + seed.charCodeAt(i)) | 0;
  return hgPalette[Math.abs(h) % hgPalette.length];
}

export const ashbrook = {
  paper: '#F7F7F7', // neutral light surface (chips)
  surface: '#FFFFFF',
  ink: '#1A1A1A', // primary text
  sub: '#555555', // secondary text
  faint: '#888888', // muted / hint text
  line: '#E5E5E5', // hairline border
  pine: '#0E46BC', // brand blue — primary actions
  pineSoft: '#EAF0FB', // light blue tint — selected / soft surfaces
  pineDeep: '#0E46BC', // brand blue — emphasis text + the big average number
  gold: '#0E46BC', // accent (eyebrow + avatar) → brand blue
  goldSoft: '#FEF6D6', // light yellow — the small "AI" chip (kept as a subtle AI signal)
  goldBorder: '#F3E3A3',
  goldInk: '#8A6A1F', // dark yellow text on the AI chip
  onGold: '#FFFFFF',
  countBg: '#EAF0FB', // light blue — the "needs grading" count chip
  countInk: '#0E46BC',
  sage: '#02C995', // brand green — progress bar ≥85%
  amber: '#E8A317', // amber — progress bar <80%
  coral: '#E5484D', // red — error
  sky: '#3CC1FF', // info sky — progress bar ≥80%
} as const;

// Scoped type scale (px). Family is Inter (set on the theme); only sizes here.
export const ashbrookType = {
  display: 44,
  title: 15,
  body: 14,
  label: 13,
  micro: 12,
  tiny: 11,
} as const;

export const gradingTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: ashbrook.pine, contrastText: '#FFFFFF' },
    secondary: { main: ashbrook.gold, contrastText: '#FFFFFF' },
    background: { default: ashbrook.surface, paper: ashbrook.surface },
    text: { primary: ashbrook.ink, secondary: ashbrook.sub },
    divider: ashbrook.line,
    success: { main: ashbrook.sage },
    warning: { main: ashbrook.amber },
    error: { main: ashbrook.coral },
  },
  typography: {
    fontFamily: 'inter',
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: { styleOverrides: { root: { borderRadius: 12, border: `1px solid ${ashbrook.line}`, boxShadow: 'none' } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
  },
});
